import React, { useState, useRef, useEffect } from "react";
import GoogleMapReact from "google-map-react";
import { fetch_from_ns } from "../../utils/axios-utils";
import "./SearchMap.scss";
import classNames from "classnames";
import useSupercluster from "use-supercluster";
import Icon from "../ui/Icon";
import { formatNumber } from "../../utils/formatting";
import { useInterval } from "../../utils/hooks-utils";
import { searchPageMapOptions } from "../../utils/google-maps";
import { FRONTEND } from "../constants";


const MAX_ZOOM = 20;

interface MapData {
  rows: any[];  // [apt_id, lng, lat, price/[bedroom, bathroom]]
}

interface ListingInfoData {
  pic?: string;
  headline?: string;
  url?: string;
  price?: any;
  currency?: string;
  address_lines?: string[];
  beds?: number;
  baths?: number;
  hampton_rental?: boolean;
}

const formatPrice = (price) => {
  switch (typeof(price)) {
    case "number":
      if (price < 1000000) {
        return `${Math.round((price / 1000) * 100) / 100}K`
      } else {
        return `${Math.round((price / 1000000) * 100) / 100}M`
      }
    case "object":
      if (price instanceof Array && (price[0] || price[1])) {
        const bed = price[0]
        const bath = price[1]
        return <div className="marker-bed-bath">
          {bed !== null ? bed > 0 ? <div>{bed} bed</div> : <div>studio</div> : null}
          {bath !== null ? <div>{bath} bath</div> : null}
        </div>
      }
  }
  return `View property`
}


const getLatLonCenterFromGeom = (coords) => {
  const arrAvg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;

  const centerLat = arrAvg(coords.map(c => c[2]));
  const centerLon = arrAvg(coords.map(c => c[1]));

  if (isNaN(centerLat) || isNaN(centerLon)) {
    return null;
  } else {
    return [centerLat, centerLon];
  }
}

const isSearchUrl = () => {
  const pathName = location.pathname.toLowerCase()
  return pathName.startsWith("/sales/") || pathName.startsWith("/rentals/")
}

export default function SearchMap() {

  const [showHideMap, setShowHideMap] = useState(false)
  const [fetchingMap, setFetchingMap] = useState(false)
  const [center, setCenter] = useState(null)
  const [cachedMapData, setCachedMapData] = useState({})
  const [cachedListingData, setCachedListingData] = useState({})
  const [points, setPoints] = useState([])
  const [zoom, setZoom] = useState(20)
  const [bounds, setBounds] = useState(null)
  const [focusedMarker, setFocusedMarker] = useState(null)
  const [clusterPage, setClusterPage] = useState(1)
  const [clusterTile, setClusterTile] = useState(null)
  const [currentUrl, setCurrentUrl] = useState("")
  const [fetchMapPointsFailed, setFetchMapPointsFailed] = useState(false)
  const [fetchingListingInfo, setFetchingListingInfo] = useState(false)
  const [listingInfoError, setListingInfoError] = useState(false)
  const [listingInfoData, setListingInfoData] : [ListingInfoData, any] = useState({})
  const [mapData, setMapData] : [MapData, any] = useState({rows: []})
  const mapRef: any = useRef();


  const toggleMap = () => {
    setShowHideMap(!showHideMap)
    if (!showHideMap) {
      fetchMapPoints()
    } else {
      // unset everything
      setFocusedMarker(null)
    }
  }

  const fetchMapPoints = () => {
    const cUrl = window.location.href
    if (fetchMapPointsFailed) {
      setFetchMapPointsFailed(false)
    }
    setFetchingMap(true)
    if (cachedMapData[cUrl] !== undefined) {
      setPoints(cachedMapData[cUrl])
      setFetchingMap(false)
      const c = getLatLonCenterFromGeom(
        cachedMapData[cUrl].map((x) => x.coordinates))
      if (c) {
        setCenter(c)
        setZoom(10)
      }
    } else {
      fetch_from_ns("GET", cUrl, {
        format: "json-map"
      }).then((response) => {
        const resp = response.data
        setMapData(resp)
        if (resp.rows) {
          // redundant now that we have boundary, but keep incase it fails?
          const c = getLatLonCenterFromGeom(resp.rows.filter(x => x[1] && x[2]))
          if (c) {
            setCenter(c)
            setZoom(10)
          }
          const p = resp.rows.filter(x => x[1] && x[2]).map((row) => ({
            type: "Feature",
            properties: {
              cluster: false,
              price: row[3],
              id: row[0]
            },
            geometry: {
              type: "Point",
              coordinates: [row[1], row[2]]
            }
          }))
          setPoints(p)
          const newCachedMapData = { ...cachedMapData}
          newCachedMapData[cUrl] = p
          setCachedMapData(newCachedMapData)
          setFetchingMap(false)
        }
      }).catch(() => {
        setFetchMapPointsFailed(true)
        setFetchingMap(false)
      })
    }
  }

  const fetchListingInfo = (webid) => {
    setFetchingListingInfo(true)
    if (cachedListingData[webid] !== undefined) {
      setListingInfoError(false)
      setListingInfoData(cachedListingData[webid])
      setFetchingListingInfo(false)
    } else {
      fetch_from_ns("GET", "/ajax/view/map_webid", {
        id: webid,
      }).then((response) => {
        const resp = response.data
        if (resp.errors !== null) {
          setListingInfoError(true)
        } else {
          setListingInfoError(false)
          setListingInfoData(resp.data || {})
          const newCachedListingData = { ...cachedListingData }
          newCachedListingData[webid] = resp.data || {}
          setCachedListingData(newCachedListingData)
        }
        setFetchingListingInfo(false)
      })
    }
  }

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 100, maxZoom: MAX_ZOOM }
  });


  const markerOnClick = (id: number) => {
    setFocusedMarker(id)
    fetchListingInfo(id)
  }

  const clusterOnClick = (clusterId: number) => {
    const ct = supercluster.getLeaves(clusterId, 100)
    setFocusedMarker(`cluster-${clusterId}`)
    setClusterTile(ct)
    setClusterPage(1)
    fetchListingInfo(ct[0].properties.id)
  }
  
  const ClusterInfoWindow = (props) => {
    const { properties } = props
    const clusterPageIsValid = clusterPage && clusterPage <= properties.point_count
    const clusterPointProperties = clusterPage ? 
                clusterPage <= properties.point_count ? clusterTile[clusterPage-1].properties : {} : {}
    return (
      clusterPageIsValid ?
      <div className="marker-info-box">
        <div className="marker-navigation">
          <button className="market-nav-prev"
            onClick={() => {
              const cp = clusterPage
              fetchListingInfo(clusterTile[cp - 2].properties.id)
              setClusterPage(cp - 1)
            }}
            disabled={clusterPage <= 1}
            >back</button>
          <div className="marker-nav-info">{clusterPage} of {properties.point_count} Listings</div>
          <button className="market-nav-next"
            onClick={() => {
              const cp = clusterPage
              fetchListingInfo(clusterTile[cp].properties.id)
              setClusterPage(cp + 1)
            }}
            disabled={clusterPage >= properties.point_count}
            >next</button>
        </div>
        {fetchingListingInfo ?
          <div className="marker-listing-info">
            <div className="ml-close" onClick={closeInfo}>
              <div className="mdiv">
                <div className="md"></div>
              </div>
            </div>
            <a href={listingInfoData.url} target="_blank">
              <div className="ml-img" style={{
                backgroundImage: `url(${`${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
              }} />
              <div className="ml-details">
                <div className="ml-webid">{`Web Id: ${clusterPointProperties.id}`}</div>
                <div className="ml-fetching">
                  <i className="fa fa-circle-o-notch fa-spin fa-2x ml2" aria-hidden="true" />
                </div>
                <div className="ml-price">{formatPrice(clusterPointProperties.price)}</div>
              </div>
            </a>
          </div>
          :
          listingInfoError === true ?
            <div className="marker-listing-info" onClick={closeInfo}>
              <div className="ml-close" onClick={closeInfo}>
                <div className="mdiv">
                  <div className="md"></div>
                </div>
              </div>
              <a href={listingInfoData.url} target="_blank">
                <div className="ml-img" style={{
                  backgroundImage: `url(${`${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                }} />
                <div className="ml-details">
                  <div className="ml-webid">{`Web Id: ${clusterPointProperties.id}`}</div>
                  <div className="ml-address">Undisclosed Address</div>
                  <div className="ml-price">{formatPrice(clusterPointProperties.price)}</div>
                </div>
              </a>
            </div>
            :
            listingInfoData.url ?
              <div className="marker-listing-info" onClick={closeInfo}>
                <div className="ml-close" onClick={closeInfo}>
                  <div className="mdiv">
                    <div className="md"></div>
                  </div>
                </div>
                <a href={listingInfoData.url} target="_blank">
                  <div className="ml-img" style={{
                    backgroundImage: `url(${listingInfoData.pic || `${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: listingInfoData.pic ? "cover" : "contain",
                  }} />
                  <div className="ml-details">
                    <div className="ml-webid">{`Web Id: ${clusterPointProperties.id}`}</div>
                    <div className="ml-headline">{listingInfoData.headline}</div>
                    <div className="ml-info">
                      {listingInfoData.address_lines ?
                        <div className="ml-address">
                          {listingInfoData.address_lines.map((l, i) =>
                            <div key={`${i}-${clusterPointProperties.id}`}>{l}</div>
                          )}
                        </div>
                        : null
                      }
                      <div className="ml-beds-baths">
                          {listingInfoData.beds !== null ? listingInfoData.beds > 0 ? <span className="mr2">{listingInfoData.beds} bed</span> : <span className="mr2">studio</span> : null}
                        {listingInfoData.baths !== null ? <span>{listingInfoData.baths} bath</span> : null}</div>
                    </div>
                    {listingInfoData.hampton_rental ?
                      <div className="ml-hmp-price">
                        <div dangerouslySetInnerHTML={{
                          __html: listingInfoData.price
                        }}></div>
                      </div>
                      :
                      <div className="ml-price">
                        {listingInfoData.currency && listingInfoData.price ? `${listingInfoData.currency}${formatNumber(listingInfoData.price)}` : null}
                      </div>
                    }
                  </div>
                </a>
              </div>
              :
              <div className="marker-listing-info" onClick={closeInfo}>
                <div className="ml-close" onClick={closeInfo}>
                  <div className="mdiv">
                    <div className="md"></div>
                  </div>
                </div>
                <a href={listingInfoData.url} target="_blank">
                  <div className="ml-img" style={{
                    backgroundImage: `url(${`${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                  }} />
                  <div className="ml-details">
                    <div className="ml-webid">{`Web Id: ${properties.id}`}</div>
                    <div className="ml-price">{formatPrice(properties.price)}</div>
                  </div>
                </a>
              </div>
            }
      </div> : <div className="marker-info-box"></div>
    )
  }

  const ClusterMarker:any = ({ children, properties, focused }) => {
    return (
      <>
        {children}
        {focused && <ClusterInfoWindow properties={properties} />}
      </>)
  };

  const closeInfo = () => {
    setFocusedMarker(null)
  }

  const InfoWindow = (props) => {
    const { properties } = props
    return (
      <div className="marker-info-box">
        {fetchingListingInfo ?
          <div className="marker-listing-info">
            <div className="ml-close" onClick={closeInfo}>
              <div className="mdiv">
                <div className="md"></div>
              </div>
            </div>
            <a href={listingInfoData.url} target="_blank">
              <div className="ml-img" style={{
                backgroundImage: `url(${`${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
              }}/>
              <div className="ml-details">
                <div className="ml-webid">{`Web Id: ${properties.id}`}</div>
                <div className="ml-fetching">
                  <i className="fa fa-circle-o-notch fa-spin fa-2x ml2" aria-hidden="true" />
                </div>
                <div className="ml-price">{formatPrice(properties.price)}</div>
              </div>
            </a>
          </div>
          :
          listingInfoError === true ?
            <div className="marker-listing-info" onClick={closeInfo}>
              <div className="ml-close" onClick={closeInfo}>
                <div className="mdiv">
                  <div className="md"></div>
                </div>
              </div>
              <a href={listingInfoData.url} target="_blank">
                <div className="ml-img" style={{
                  backgroundImage: `url(${`${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "contain",
                }} />
                <div className="ml-details">
                  <div className="ml-webid">{`Web Id: ${properties.id}`}</div>
                  <div className="ml-address">Undisclosed Address</div>
                  <div className="ml-price">{formatPrice(properties.price)}</div>
                </div>
              </a>
            </div>
            :
            listingInfoData.url ?
              <div className="marker-listing-info" onClick={closeInfo}>
                <div className="ml-close" onClick={closeInfo}>
                  <div className="mdiv">
                    <div className="md"></div>
                  </div>
                </div>
                <a href={listingInfoData.url} target="_blank">
                  <div className="ml-img" style={{
                    backgroundImage: `url(${listingInfoData.pic || `${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: listingInfoData.pic ? "cover" : "contain",
                  }}/>
                  <div className="ml-details">
                    <div className="ml-webid">{`Web Id: ${properties.id}`}</div>
                    <div className="ml-headline">{listingInfoData.headline}</div>
                    <div className="ml-info">
                      {listingInfoData.address_lines ? 
                        <div className="ml-address">
                        {listingInfoData.address_lines.map((l, i) =>
                          <div key={`${i}-${properties.id}`}>{l}</div>
                        )}
                        </div>
                        : null
                      }
                      <div className="ml-beds-baths">
                        {listingInfoData.beds !== null ? listingInfoData.beds > 1 ? <span className="mr2">{listingInfoData.beds} bed</span> : <span className="mr2">studio</span> : null}
                        {listingInfoData.baths !== null ? <span>{listingInfoData.baths} bath</span> : null}</div>
                      </div>
                      {listingInfoData.hampton_rental ?
                        <div className="ml-hmp-price">
                          <div dangerouslySetInnerHTML={{
                            __html: listingInfoData.price}}></div>
                        </div>
                        :
                        <div className="ml-price">
                          {listingInfoData.currency && listingInfoData.price ? `${listingInfoData.currency}${formatNumber(listingInfoData.price)}`: null}
                        </div>
                      }
                    </div>
                  </a>
              </div>
              :
              <div className="marker-listing-info" onClick={closeInfo}>
                <div className="ml-close" onClick={closeInfo}>
                  <div className="mdiv">
                    <div className="md"></div>
                  </div>
                </div>
                <a href={listingInfoData.url} target="_blank">
                  <div className="ml-img" style={{
                    backgroundImage: `url(${`${FRONTEND}wwwimages/nslogos/ns-square-logo.svg`}`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                  }} />
                  <div className="ml-details">
                    <div className="ml-webid">{`Web Id: ${properties.id}`}</div>
                    <div className="ml-price">{formatPrice(properties.price)}</div>
                  </div>
                </a>
              </div>
        }
      </div>
    )
  }

  const Marker:any = ({ children, properties, focused }) => {
    return (
    <>
      {children}
        {focused && <InfoWindow properties={properties} />}
    </>)
  };


  // can't avoid polling when you're using angular to change url (fixable when we switch to react)
  useInterval(() => {
    const url = window.location.href
    if (url !== currentUrl) {
      setCurrentUrl(url)
      if (showHideMap && isSearchUrl()) {
        fetchMapPoints()
      }
    }
    }, 1000);

  const getMapBounds = (map, maps, places) => {
    const b = new maps.LatLngBounds();

    places.forEach((place) => {
      b.extend(new maps.LatLng(
        place[2],
        place[1],
      ));
    });
    return b;
  };

  const apiIsLoaded = (map, maps, places) => {
    // Get bounds by our places
    const b = getMapBounds(map, maps, places);
    // Fit map to bounds
    map.fitBounds(b);
    // Bind the resize listener
    // bindResizeListener(map, maps, b);
  };

  // // Re-center map when resizing the window
  // const bindResizeListener = (map, maps, b) => {
  //   maps.event.addDomListenerOnce(map, "idle", () => {
  //     maps.event.addDomListener(window, "resize", () => {
  //       map.fitBounds(b);
  //     });
  //   });
  // };

  return (
    <div>
      {showHideMap && <style dangerouslySetInnerHTML={{
          __html: `
        .title.pt3-ns.ng-binding { display:none; }
        .search_area.region { display:none; }
        // .search-results.resultSet { display:none; }
        .links_footer.row-fluid.noprint.hidden-phone { display:none; }
        #search-page .userMenuContent {min-height: unset !important; }
      `}}>
      </style>}
      <div
        id="ns-search-map-button"
        className={classNames({ "ns-search-map-button-clicked": center && showHideMap})}
        onClick={toggleMap}
        // style={{display: "none"}}
        >
        <div>
          <Icon name="map"/>
          <span>{showHideMap ? "Hide" : "View"} Map</span>
        </div>
      </div>
      {fetchingMap && center && showHideMap ?
        <div className="ns-search-map-fetching">
            <i className="fa fa-circle-o-notch fa-spin fa-2x ml2" aria-hidden="true" />
        </div>
        :
        null
      }
      {showHideMap &&
        <div id="ns-search-map-container">
          {center ?
            <GoogleMapReact
              bootstrapURLKeys={{ key: [__GOOGLE_MAPS_API_KEY__] }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map, maps }) => {
                mapRef.current = map;
                apiIsLoaded(map, maps, mapData.rows.filter((x) => x[1] && x[2]))
              }}
              defaultCenter={center}
              center={center}
              defaultZoom={10}
              zoom={zoom}
              options={searchPageMapOptions}
              onChange={({ zoom, bounds, center }) => {
                setCenter(center)
                setZoom(zoom);
                setBounds([
                  bounds.nw.lng,
                  bounds.se.lat,
                  bounds.se.lng,
                  bounds.nw.lat
                ]);
              }}
            >
            {clusters.map(cluster => {
              const [longitude, latitude] = cluster.geometry.coordinates;
              const {
                cluster: isCluster,
                point_count: pointCount
              } = cluster.properties;

              if (isCluster) {
                return (
                  <ClusterMarker
                    key={`cluster-${cluster.id}`}
                    lat={latitude}
                    lng={longitude}
                    focused={focusedMarker === `cluster-${cluster.properties.cluster_id}`}
                    properties={cluster.properties}
                  >
                    {zoom < MAX_ZOOM ? 
                      <div
                        className="cluster-marker"
                        style={{
                          width: `${10 + (pointCount / points.length) * 20}px`,
                          height: `${10 + (pointCount / points.length) * 20}px`
                        }}
                        onClick={() => {
                          const expansionZoom = Math.min(
                              supercluster.getClusterExpansionZoom(cluster.id),
                              20
                            );
                          mapRef.current.setZoom(expansionZoom);
                          mapRef.current.panTo({ lat: latitude, lng: longitude })
                        }}
                      >
                        {pointCount}
                      </div>
                      :
                      <button
                        className={classNames("listing-marker", { "focused-marker": focusedMarker === `cluster-${cluster.properties.cluster_id}` })}
                        onClick={() => { console.log(cluster); clusterOnClick(cluster.properties.cluster_id) }}>
                        {pointCount} Listings
                      </button>
                    }
                  </ClusterMarker>
                );
              }

              return (
                <Marker
                  key={`${cluster.properties.id}`}
                  lat={latitude}
                  lng={longitude}
                  focused={focusedMarker === cluster.properties.id}
                  properties={cluster.properties}
                >
                  <button
                    className={classNames("listing-marker", { "focused-marker": focusedMarker === cluster.properties.id})}
                    onClick={() => { markerOnClick(cluster.properties.id)}}>
                    {formatPrice(cluster.properties.price)}
                  </button>
                </Marker>
              );
            })}
            </GoogleMapReact>
            :
          fetchMapPointsFailed ?
            <div className="tc center ma1" style={{
                width: "100%", height: "100%", position: "relative",
                display: "flex", justifyContent: "center",
                alignItems: "center"}}>
              Failed to load map, please try again later.
            </div>
            :
            <div className="tc center ma1" style={{
                width: "100%", height: "100%", position: "relative",
                display: "flex", justifyContent: "center",
                alignItems: "center", background: "lightgray"}}>
              Loading Map ...
              <i className="fa fa-circle-o-notch fa-spin fa-2x ml2" aria-hidden="true" />
            </div>
          }
        </div>
      }
    </div>
  )
}
